<template>
  <div v-if="visible" class="bug-modal-overlay" @click.self="close">
    <div class="bug-modal">
      <div class="bug-modal__header">
        <h2>Report a bug</h2>
        <button class="bug-modal__close" type="button" @click="close">×</button>
      </div>

      <form class="bug-modal__body" @submit.prevent="submit">
        <div class="bug-field">
          <label for="bug-summary">What went wrong? <span class="req">*</span></label>
          <input
            id="bug-summary"
            v-model.trim="form.summary"
            type="text"
            maxlength="200"
            placeholder="Short summary of the issue"
          />
        </div>

        <div class="bug-field">
          <label for="bug-steps">Steps to reproduce <span class="req">*</span></label>
          <textarea
            id="bug-steps"
            v-model.trim="form.steps"
            rows="4"
            placeholder="1. I opened...
2. I clicked...
3. It exploded emotionally"
          />
        </div>

        <div class="bug-grid">
          <div class="bug-field">
            <label for="bug-expected">What did you expect? <span class="req">*</span></label>
            <textarea
              id="bug-expected"
              v-model.trim="form.expected"
              rows="3"
              placeholder="What should have happened?"
            />
          </div>

          <div class="bug-field">
            <label for="bug-actual">What actually happened? <span class="req">*</span></label>
            <textarea
              id="bug-actual"
              v-model.trim="form.actual"
              rows="3"
              placeholder="What happened instead?"
            />
          </div>
        </div>

        <div class="bug-grid">
          <div class="bug-field">
            <label for="bug-frequency">How often have you seen this? <span class="req">*</span></label>
            <select id="bug-frequency" v-model="form.frequency">
              <option disabled value="">Please choose</option>
              <option value="first_time">First time</option>
              <option value="second_time">Second time</option>
              <option value="sometimes">Sometimes</option>
              <option value="always">Always</option>
            </select>
          </div>

        </div>

        <div class="bug-field">
          <label for="bug-screenshot">Screenshot (optional)</label>
          <input
            id="bug-screenshot"
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="onFileChange"
          />
          <div v-if="fileName" class="bug-file-name">
            Attached: {{ fileName }}
            <button type="button" class="bug-link-button" @click="removeFile">Remove</button>
          </div>
        </div>

        <div class="bug-field">
          <label for="bug-notes">Anything else?</label>
          <textarea
            id="bug-notes"
            v-model.trim="form.notes"
            rows="3"
            placeholder="Browser? Page? Weird timing? Mercury in retrograde?"
          />
        </div>

        <div v-if="error" class="bug-error">
          {{ error }}
        </div>

        <div class="bug-modal__footer">
          <button type="button" class="btn btn-secondary" @click="close" :disabled="submitting">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            {{ submitting ? 'Sending...' : 'Submit bug report' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BugReportModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    endpoint: {
      type: String,
      default: '/api/bug-reports/'
    },
    autoSubmit: {
      type: Boolean,
      default: true
    },
    extraContext: {
      type: Object,
      default: function () {
        return {}
      }
    }
  },
  data() {
    return {
      submitting: false,
      error: '',
      selectedFile: null,
      fileName: '',
      form: this.getDefaultForm()
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.error = ''
      }
    }
  },
  methods: {
    getDefaultForm() {
      return {
        summary: '',
        steps: '',
        expected: '',
        actual: '',
        frequency: '',
        email: '',
        notes: ''
      }
    },

    close() {
      if (this.submitting) return
      this.$emit('close')
    },

    reset() {
      this.form = this.getDefaultForm()
      this.error = ''
      this.submitting = false
      this.selectedFile = null
      this.fileName = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    validate() {
      if (!this.form.summary) return 'Please add a short summary.'
      if (!this.form.steps) return 'Please describe the steps to reproduce.'
      if (!this.form.expected) return 'Please say what you expected to happen.'
      if (!this.form.actual) return 'Please say what actually happened.'
      if (!this.form.frequency) return 'Please choose how often you have seen this.'
      return ''
    },

    onFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        this.selectedFile = null
        this.fileName = ''
        return
      }

      if (!file.type.startsWith('image/')) {
        this.error = 'Screenshot must be an image file.'
        event.target.value = ''
        return
      }

      if (file.size > 8 * 1024 * 1024) {
        this.error = 'Screenshot must be smaller than 8MB.'
        event.target.value = ''
        return
      }

      this.error = ''
      this.selectedFile = file
      this.fileName = file.name
    },

    removeFile() {
      this.selectedFile = null
      this.fileName = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    getClientContext() {
      const posthogSessionId =
        window.posthog && typeof window.posthog.get_session_id === 'function'
          ? window.posthog.get_session_id()
          : null

      const sentryLastEventId =
        window.Sentry && typeof window.Sentry.lastEventId === 'function'
          ? window.Sentry.lastEventId()
          : null

      return {
        url: window.location.href,
        path: window.location.pathname,
        userAgent: window.navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        referrer: document.referrer || '',
        timestamp: new Date().toISOString(),
        posthogSessionId: posthogSessionId,
        sentryLastEventId: sentryLastEventId,
        extraContext: this.extraContext
      }
    },

    buildFormData() {
      const fd = new FormData()

      const payload = {
        ...this.form,
        context: this.getClientContext()
      }

      fd.append('payload', JSON.stringify(payload))

      if (this.selectedFile) {
        fd.append('screenshot', this.selectedFile)
      }

      return fd
    },

    async submit() {
      this.error = this.validate()
      if (this.error) return

      const payload = {
        ...this.form,
        context: this.getClientContext()
      }

      this.$emit('submit', payload)

      if (!this.autoSubmit) return

      this.submitting = true

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          body: this.buildFormData(),
          credentials: 'include'
        })

        if (!response.ok) {
          let message = 'Could not submit bug report.'
          try {
            const data = await response.json()
            message = data.detail || data.error || message
          } catch (e) {
            // ignore parse failure
          }
          throw new Error(message)
        }

        this.$emit('submitted')
        this.reset()
        this.close()
      } catch (err) {
        this.error = err && err.message ? err.message : 'Something went wrong.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.bug-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.bug-modal {
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.bug-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid #eee;
}

.bug-modal__header h2 {
  margin: 0;
  font-size: 20px;
}

.bug-modal__close {
  border: 0;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.bug-modal__body {
  padding: 20px;
}

.bug-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.bug-field {
  margin-bottom: 16px;
}

.bug-field label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

.bug-field input,
.bug-field textarea,
.bug-field select {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
  font: inherit;
  background: #fff;
}

.bug-field textarea {
  resize: vertical;
}

.req {
  color: #c62828;
}

.bug-file-name {
  margin-top: 8px;
  font-size: 14px;
}

.bug-link-button {
  margin-left: 8px;
  border: 0;
  background: none;
  color: #1565c0;
  cursor: pointer;
  padding: 0;
}

.bug-error {
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdecec;
  color: #b42318;
}

.bug-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
}

.btn {
  border: 0;
  border-radius: 8px;
  padding: 10px 16px;
  font: inherit;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.btn-primary {
  background: #222;
  color: #fff;
}

.btn-secondary {
  background: #f3f4f6;
  color: #222;
}

@media (max-width: 700px) {
  .bug-grid {
    grid-template-columns: 1fr;
  }

  .bug-modal {
    max-height: 95vh;
  }
}
</style>